import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Calendar, Clock, MapPin, ArrowLeft } from 'lucide-react'
import { eventsApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import MediaImage from '../../components/ui/MediaImage'
import Button from '../../components/ui/Button'
import CmsHtmlContent from '../../components/cms/CmsHtmlContent'
import { fadeInUp } from '../../utils/animations'
import { formatDate } from '../../utils/date'

export default function EventDetail() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const { localized, isRTL } = useLanguage()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    eventsApi
      .getBySlug(slug)
      .then(({ data }) => setEvent(data.data || data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center header-offset">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center header-offset">
        <p className="text-text/60">{t('pages.notFound')}</p>
        <Button to="/events" variant="primary" className="mt-4">
          {t('buttons.back')}
        </Button>
      </div>
    )
  }

  const content = localized(event, 'content') || localized(event, 'description')

  return (
    <article className="header-offset">
      <div className="relative h-64 overflow-hidden sm:h-80">
        <MediaImage
          src={event.image || event.featured_image}
          alt={localized(event, 'title')}
          fallbackIndex={2}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        <Link
          to="/events"
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent"
        >
          <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
          {t('buttons.back')}
        </Link>

        <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
          <h1 className="mb-6 text-2xl font-bold text-primary sm:text-3xl md:text-4xl">
            {localized(event, 'title')}
          </h1>

          <div className="mb-8 flex flex-col gap-4 rounded-2xl border border-border bg-background p-5 sm:flex-row sm:flex-wrap sm:gap-6 sm:p-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-text/50">
                {t('events.date')}
              </div>
              <div className="mt-1 flex items-center gap-2 font-medium text-primary">
                <Calendar className="h-4 w-4 text-accent" />
                {(event.start_date || event.event_date) &&
                  formatDate(event.start_date || event.event_date)}
              </div>
            </div>
            {event.time && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text/50">
                  {t('events.time')}
                </div>
                <div className="mt-1 flex items-center gap-2 font-medium text-primary">
                  <Clock className="h-4 w-4 text-accent" />
                  {event.time}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-text/50">
                {t('events.location')}
              </div>
              <div className="mt-1 flex items-center gap-2 font-medium text-primary">
                <MapPin className="h-4 w-4 text-accent" />
                {localized(event, 'location')}
              </div>
            </div>
          </div>

          {event.content_en || event.content_ar ? (
            <CmsHtmlContent content={content} className="prose prose-lg max-w-none" />
          ) : (
            <p className="text-lg leading-relaxed text-text/75">{content}</p>
          )}

          <Button to="/contact" variant="accent" size="lg" className="mt-8 w-full sm:w-auto">
            {t('events.register')}
          </Button>
        </motion.div>
      </div>
    </article>
  )
}
